
import { useState, useEffect } from 'react';
import { reportAPI } from '../../services/api';
import {
  TEST_PRICE_INR,
  TEST_TIME_LIMIT_MINUTES,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
  SUPPORT_WHATSAPP_URL,
  SUPPORT_EMAIL,
} from '../../constants/app';

export default function PaymentSuccess() {
  const [downloading, setDownloading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const isCouponUnlock = paymentInfo?.method === 'coupon';
  const testTakerName: string = (() => {
    if (paymentInfo?.testTakerName) return String(paymentInfo.testTakerName);
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u?.name && !/^User \d{4}$/i.test(u.name) ? String(u.name) : '';
    } catch {
      return '';
    }
  })();
  const [testId, setTestId] = useState(null);

  useEffect(() => {
    const storedPaymentInfo = sessionStorage.getItem('paymentInfo');
    const storedTestId =
      sessionStorage.getItem('submittedTestId') ||
      sessionStorage.getItem('currentTestId');

    if (!storedPaymentInfo) {
      console.warn('No payment info — redirecting to payment page');
      window.REACT_APP_NAVIGATE?.('/payment');
      return;
    }

    if (storedPaymentInfo) {
      setPaymentInfo(JSON.parse(storedPaymentInfo));
    }

    if (storedTestId) {
      setTestId(storedTestId);
    }
  }, []);

  const handleDownloadReport = async () => {
    if (!testId) {
      console.error('❌ Cannot download: No test ID');
      alert('Test ID not found. Please complete a test first or contact support.');
      return;
    }
    
    console.log('📥 Starting PDF download for test:', testId);

    try {
      setDownloading(true);
      console.log('📥 Downloading PDF report for test:', testId);
      
      // Download PDF from backend
      const blob = await reportAPI.downloadPDF(testId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Career_Report_${testId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ PDF downloaded successfully');
    } catch (error: any) {
      console.error('Download error:', error);
      const msg = error?.message || '';
      if (msg.includes('Payment') || msg.includes('402')) {
        alert('Payment required before downloading. Please complete payment first.');
        window.REACT_APP_NAVIGATE?.('/payment');
      } else {
        alert('Failed to download report. Please try again or contact support.');
      }
    } finally {
      setDownloading(false);
    }
  };

  // Commented out for now
  // const handleSendToWhatsApp = () => {
  //   const message = "I've completed my Vijna X psychometric assessment! Check out my personalized career report.";
  //   const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  //   window.open(whatsappUrl, '_blank');
  // };

  // const handleSendToEmail = () => {
  //   const subject = "My Vijna X Psychometric Assessment Report";
  //   const body = "Hi,\n\nI've completed my psychometric assessment with Vijna X. Please find my personalized career report attached.\n\nBest regards";
  //   const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  //   window.location.href = mailtoUrl;
  // };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
          {/* Success Animation */}
          <div className="mb-8">
            <div className="w-24 h-24 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6 animate-pulse">
              <i className="ri-check-line w-12 h-12 flex items-center justify-center text-green-600 text-4xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isCouponUnlock ? 'Report Unlocked!' : 'Payment Successful!'}
            </h1>
            <p className="text-gray-600 text-lg">
              {testTakerName ? `${testTakerName.split(' ')[0]}, your` : 'Your'} {TEST_TIME_LIMIT_MINUTES}-minute psychometric assessment is complete.
            </p>
            {testTakerName && (
              <p className="mt-3 inline-flex items-center bg-blue-50 border border-blue-200 text-blue-800 text-sm font-medium px-4 py-2 rounded-full" data-testid="report-name">
                <i className="ri-file-user-line mr-2"></i>
                Report will be generated for: <strong className="ml-1">{testTakerName}</strong>
              </p>
            )}
          </div>

          {/* Payment Details */}
          <div className="bg-gray-50 rounded-2xl p-6 mb-8">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Transaction ID</p>
                <p className="font-semibold text-gray-900 text-xs break-all">
                  {paymentInfo?.paymentId ? `#${paymentInfo.paymentId.substring(0, 20)}...` : '#VX2024001234'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Amount Paid</p>
                <p className="font-semibold text-gray-900">
                  ₹{paymentInfo?.amount ?? TEST_PRICE_INR}
                  {isCouponUnlock && paymentInfo?.couponCode && (
                    <span className="ml-2 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                      Coupon {paymentInfo.couponCode} applied
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Payment Method</p>
                <p className="font-semibold text-gray-900">
                  {isCouponUnlock
                    ? `Coupon (${paymentInfo?.couponCode || '100% off'})`
                    : paymentInfo?.method ? paymentInfo.method.toUpperCase() : 'UPI'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Date & Time</p>
                <p className="font-semibold text-gray-900">
                  {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>

          {/* Report Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Your Report Contains:</h3>
            <div className="grid md:grid-cols-2 gap-4 text-left">
              <div className="flex items-center">
                <i className="ri-user-3-line w-5 h-5 flex items-center justify-center text-blue-600 mr-3"></i>
                <span className="text-gray-700">Detailed Personality Analysis</span>
              </div>
              <div className="flex items-center">
                <i className="ri-briefcase-line w-5 h-5 flex items-center justify-center text-blue-600 mr-3"></i>
                <span className="text-gray-700">Career Recommendations</span>
              </div>
              <div className="flex items-center">
                <i className="ri-book-line w-5 h-5 flex items-center justify-center text-blue-600 mr-3"></i>
                <span className="text-gray-700">Subject Selection Guide</span>
              </div>
              <div className="flex items-center">
                <i className="ri-lightbulb-line w-5 h-5 flex items-center justify-center text-blue-600 mr-3"></i>
                <span className="text-gray-700">Learning Style Assessment</span>
              </div>
              <div className="flex items-center">
                <i className="ri-road-map-line w-5 h-5 flex items-center justify-center text-blue-600 mr-3"></i>
                <span className="text-gray-700">Future Planning Roadmap</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              onClick={handleDownloadReport}
              disabled={downloading || !testId}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 disabled:transform-none whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
            >
              {downloading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                  Generating PDF...
                </div>
              ) : (
                <>
                  <i className="ri-download-line w-6 h-6 flex items-center justify-center mr-2 inline-block"></i>
                  Download Report PDF
                </>
              )}
            </button>

            {/* Commented out for now
            <div className="grid md:grid-cols-2 gap-4">
              <button
                onClick={handleSendToWhatsApp}
                className="bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center mr-2 inline-block"></i>
                Share on WhatsApp
              </button>

              <button
                onClick={handleSendToEmail}
                className="bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line w-5 h-5 flex items-center justify-center mr-2 inline-block"></i>
                Send to Email
              </button>
            </div>
            */}
          </div>

          {/* Support Information */}
          <div className="mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-2xl">
            <h4 className="font-semibold text-yellow-800 mb-2">Need Help Understanding Your Report?</h4>
            <p className="text-yellow-700 text-sm mb-3">
              Our career counselors are available to help you interpret your results and plan your next steps.
            </p>
            <div className="flex justify-center space-x-4 text-sm">
              <a href={SUPPORT_PHONE_TEL} className="text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap cursor-pointer">
                📞 {SUPPORT_PHONE_DISPLAY}
              </a>
              <span className="text-gray-400">|</span>
              <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-medium whitespace-nowrap cursor-pointer">
                💬 WhatsApp
              </a>
              <span className="text-gray-400">|</span>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap cursor-pointer">
                ✉️ {SUPPORT_EMAIL}
              </a>
            </div>
          </div>

          {/* Return Home */}
          <div className="mt-8 text-center">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap cursor-pointer"
            >
              ← Return to Home
            </button>
          </div>
        </div>

        {/* Thank You Message */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            Thank you for choosing <span className="font-bold text-blue-600">Vijna X</span> for your career guidance journey!
          </p>
        </div>
      </div>
    </div>
  );
}
